import type { Recipe } from '../types';

// Korean-style gochujang ground beef, scaled to 3 lb for the freezer. The
// fastest weeknight meal in the book after Mongolian beef. Carrot and onion
// melt into the sauce; heat stays mild in the pot, more gochujang at the table.

export const koreanBeefBowls: Recipe = {
  slug: 'korean-beef-bowls',
  title: 'Korean Beef Bowls',
  meal: 'dinner',
  category: 'meal',
  types: ['stir-fry', 'rice-bowl'],
  cuisines: ['korean'],
  traits: ['easy-prep', 'freezer-friendly', 'hidden-veg', 'high-protein', 'takeout-style', 'make-ahead'],
  yield: '8 servings',
  activeTime: '25 min',
  totalTime: '30 min',
  freezerLife: '3 months',
  difficulty: 1,

  summary:
    "Three pounds of ground beef browned hard, then tossed in a sweet, salty, gently spicy gochujang glaze with grated carrot, peas and green onion. One big pan, 30 minutes, eight bowls. Rice gets cooked and frozen on its own.",

  flavorNotes:
    "Sweet and savory first, then a slow, warm, mild heat from the gochujang, with garlic and fresh ginger underneath and toasted sesame on top. The beef should have crispy brown bits, not look gray and boiled — that is where most of the flavor comes from. The sauce should be glossy and cling to the meat, not pool in the bottom of the bowl. Kids and mild-eaters are fine with the pot as written; anyone who wants it hotter adds gochujang at the table.",

  ingredients: [
    {
      id: 'soy',
      name: 'Soy sauce',
      total: '½ cup',
      uses: [{ step: 1, amount: '½ cup', note: 'into the sauce' }],
    },
    {
      id: 'gochujang',
      name: 'Gochujang (Korean red chili paste)',
      total: '¼ cup',
      prep: 'paste in a tub, not a squeeze-bottle sauce',
      shop: "Mother-in-Law's Gochujang Original fermented chile paste, 10 oz (no corn syrup). If Walmart doesn't carry it, any gochujang PASTE tub",
      uses: [{ step: 1, amount: '¼ cup', note: 'mild in the pot — more at the table' }],
    },
    {
      id: 'brown-sugar',
      name: 'Brown sugar',
      total: '¼ cup',
      uses: [{ step: 1, amount: '¼ cup' }],
    },
    {
      id: 'sesame-oil',
      name: 'Toasted sesame oil',
      total: '2 tbsp',
      shop: 'Toasted sesame oil, 5 fl oz bottle (label should say 100% sesame oil)',
      uses: [{ step: 1, amount: '2 tbsp' }],
    },
    {
      id: 'cornstarch',
      name: 'Cornstarch',
      total: '1 tbsp',
      uses: [{ step: 1, amount: '1 tbsp', note: 'makes the sauce cling' }],
    },
    {
      id: 'water',
      name: 'Water',
      total: '½ cup',
      uses: [{ step: 1, amount: '½ cup' }],
    },
    {
      id: 'beef',
      name: 'Ground beef',
      total: '3 lb',
      prep: 'thawed in the fridge overnight',
      fromFreezer: true,
      uses: [{ step: 2, amount: '3 lb' }],
    },
    {
      id: 'onion',
      name: 'Yellow onion',
      total: '1 large',
      prep: 'diced small',
      shop: 'Fresh yellow onion, each (1)',
      uses: [{ step: 3, amount: '1 large' }],
    },
    {
      id: 'carrots',
      name: 'Carrots',
      total: '3 medium',
      prep: 'peeled and grated on the small holes of a box grater',
      uses: [{ step: 3, amount: '3 medium', note: 'disappears into the sauce' }],
    },
    {
      id: 'garlic',
      name: 'Garlic',
      total: '8 cloves',
      prep: 'minced or pressed',
      uses: [{ step: 3, amount: '8 cloves' }],
    },
    {
      id: 'ginger',
      name: 'Fresh ginger',
      total: '2 tbsp',
      prep: 'peeled with a spoon and grated',
      uses: [{ step: 3, amount: '2 tbsp' }],
    },
    {
      id: 'peas',
      name: 'Frozen peas',
      total: '1½ cups',
      prep: 'straight from frozen is fine',
      shop: 'Great Value frozen sweet peas, 12 oz bag',
      uses: [{ step: 4, amount: '1½ cups' }],
    },
    {
      id: 'green-onions',
      name: 'Green onions',
      total: '1 bunch',
      prep: 'sliced; keep the white and green parts in separate piles',
      uses: [
        { step: 4, amount: 'the white parts', note: 'into the pan' },
        { step: 6, amount: 'the green parts', note: 'on top' },
      ],
    },
    {
      id: 'sesame-seeds',
      name: 'Sesame seeds',
      total: '1 tbsp',
      optional: true,
      optionalKind: 'garnish',
      optionalWhy: 'A little crunch and toasty flavor on top. Skip it and nothing is lost.',
      uses: [{ step: 6, amount: 'a sprinkle', note: 'at the table' }],
    },
    {
      id: 'rice',
      name: 'Jasmine rice',
      total: '3 cups dry',
      optional: true,
      optionalKind: 'serve-with',
      optionalWhy:
        'Cook a big pot and freeze it in its own portioned bags. Rice frozen sitting in sauce goes mushy, so it always stays separate.',
      uses: [{ step: 5, amount: '3 cups dry', note: 'cooked and frozen on its own' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Whisk the sauce',
      time: '3 min',
      ingredientIds: ['soy', 'gochujang', 'brown-sugar', 'sesame-oil', 'cornstarch', 'water'],
      body:
        "In a bowl whisk {½ cup|soy} of soy sauce, {¼ cup|gochujang} of gochujang, {¼ cup|brown-sugar} of brown sugar, {2 tbsp|sesame-oil} of sesame oil, {1 tbsp|cornstarch} of cornstarch and {½ cup|water} of water until smooth with no lumps of paste left. Set it next to the stove.",
      tip:
        "Gochujang is thick — mash it against the side of the bowl with the whisk. Brands vary in heat; this amount is mild with most of them. Taste the sauce: if it is too spicy for the house, add another spoon of brown sugar.",
    },
    {
      n: 2,
      title: 'Brown the beef hard, in two batches',
      time: '12 min',
      critical: true,
      ingredientIds: ['beef'],
      body:
        "Heat your biggest skillet or a Dutch oven over medium-high. Add half of the {3 lb|beef} of ground beef, press it flat into one big patty and leave it alone for 4 to 5 minutes until the bottom has a dark brown crust. Then break it up and cook 2 to 3 minutes more until no pink is left. Move it to a bowl and repeat with the rest. Pour off all but about 1 tablespoon of the fat.",
      tip:
        "Don't skip the two batches. Three pounds in one pan steams instead of browns — gray, wet, flavorless beef. The undisturbed time is what makes the crispy bits.",
    },
    {
      n: 3,
      title: 'Soften the vegetables and aromatics',
      time: '6 min',
      ingredientIds: ['onion', 'carrots', 'garlic', 'ginger'],
      body:
        "In the same pan, with the beef out, cook the onion and the grated carrots in the leftover fat for about 5 minutes, scraping up the brown bits, until soft. Add the garlic and ginger and stir for 1 minute — just until it smells strong. Turn the heat to medium if the garlic starts to brown.",
    },
    {
      n: 4,
      title: 'Sauce it and finish',
      time: '4 min',
      ingredientIds: ['peas', 'green-onions'],
      body:
        "Put all the beef back in the pan. Whisk the sauce once more (the cornstarch settles), pour it in and stir. Let it bubble 2 to 3 minutes until it turns glossy and thick and coats the meat. Stir in {1½ cups|peas} of frozen peas and the white parts of the green onions, cook 1 more minute, and turn off the heat.",
    },
    {
      n: 5,
      title: 'Rice on the side',
      time: '20 min',
      ingredientIds: ['rice'],
      body:
        "Cook {3 cups|rice} of jasmine rice your usual way (rice cooker, or the package directions). This is for eating now and for its own freezer bags — never pack rice in the same bag as the beef.",
    },
    {
      n: 6,
      title: 'Build the bowls',
      ingredientIds: ['green-onions', 'sesame-seeds'],
      body:
        "Rice in the bowl, beef on top, then the green onion tops and a sprinkle of sesame seeds. Put the gochujang tub on the table for anyone who wants more heat.",
    },
  ],

  freezing:
    "Cool the beef completely. Portion about 1 cup per freezer bag or container, press the bags flat, label and freeze up to 3 months. Freeze rice separately in flat bags of about 1 cup each. Flat bags thaw and reheat in minutes and stack neatly.",

  reheat: [
    {
      method: 'Microwave',
      best: true,
      detail:
        "Thaw overnight in the fridge if you can. Put the beef in a bowl with a splash of water, cover, and microwave 2 to 3 minutes, stirring halfway. Rice from frozen: open the bag a crack, sprinkle a few drops of water, 2 minutes.",
    },
    {
      method: 'Skillet',
      detail:
        "Thawed beef in a skillet over medium with 1 to 2 tablespoons of water, 4 to 5 minutes, stirring, until hot and glossy again.",
    },
  ],

  glossary: [
    {
      term: 'gochujang',
      plain:
        "A thick, sticky Korean red chili paste made from chili, rice and fermented soybeans. Sweet, savory and spicy all at once. Comes in a tub; keeps for months in the fridge once opened.",
    },
    {
      term: 'crust',
      plain:
        "The dark brown layer that forms when meat sits on a hot pan without moving. That browning is flavor — it is the difference between rich-tasting beef and bland, gray beef.",
    },
    {
      term: 'grated',
      plain:
        "Rubbed across a box grater into tiny shreds. Grated carrot cooks down so fully in a sauce that nobody sees or notices it — that is the point.",
    },
  ],

  inspiration: [
    { label: 'Foodie Crush — Korean ground beef bowls', url: 'https://www.foodiecrush.com/korean-ground-beef-bowls/' },
    { label: 'Chili Pepper Madness — Gochujang ground beef bowls', url: 'https://www.chilipeppermadness.com/recipes/gochujang-ground-beef-bowls/' },
    { label: 'Spend With Pennies — Korean beef bowl', url: 'https://www.spendwithpennies.com/korean-beef-bowl/' },
  ],

  whereWeVeered:
    "The originals are 1 lb weeknight recipes for four. We tripled it for the freezer, browned the beef in two batches so it still gets a crust, and added grated carrot as the hidden vegetable. Gochujang is held to a mild amount for a shared pot (Foodie Crush uses about twice as much per pound); the tub goes on the table instead. A spoon of cornstarch and a little water keep the sauce glossy after freezing so it does not dry out on reheat. Rice is frozen separately on purpose.",
};
